import { Button, Dropdown, DropdownItem, Panel, Textbox, TextTabs, ToggleRow, type TextTab } from '@sorrem-client/ui/components';
import { useAppNavigate, useSelectedCharacterId } from '@sorrem-client/ui/hooks';
import { resolveApiErrorMessage, useCreateCrucibleMutation } from '@sorrem-client/ui/store';
import {
    formatRecommendedReadiness,
    LIMITED_CRUCIBLE_SATURATION,
    resolvePlayerCap,
    resolveRecommendedVeilSaturation,
    resolveVeilSaturationById,
    VEIL_SATURATION_BANDS,
    type CrucibleFlavorPreview,
    type CrucibleRecipeSummary,
    type CrucibleTemplate,
    type VeilSaturationId,
} from '@sorrem/shared';
import { useCallback, useEffect, useState } from 'react';
import { useCreateCrucibleOptions, useCrucibleFlavorPreview } from '@sorrem-client/ui/features/CharacterHome/hooks';
import styles from './CreateCruciblePanel.module.scss';
import { toCruciblePath } from '@sorrem-client/ui/features/CharacterHome/crucible-links';

/** The saturation picked, or `random` for today's weighted roll (#206). */
export type SaturationChoice = VeilSaturationId | 'random';

/** A crucible with a newly generated map, or a hand-made one (#223). */
export type CrucibleKind = 'random' | 'authored';

/** What the form would create right now, for the Crucible Info preview beside it (#224). */
export interface CrucibleCreateDraft {
    readonly kind: CrucibleKind;
    /** The name typed, or empty for the size's or crucible's own. */
    readonly name: string;
    readonly recipe: CrucibleRecipeSummary | null;
    readonly template: CrucibleTemplate | null;
    readonly saturation: SaturationChoice;
    readonly isSolo: boolean;
    readonly maxPlayers: number;
    /** A random crucible's rolled name, backstory, place and saturation (#225), or null. */
    readonly flavor: CrucibleFlavorPreview | null;
}

interface CreateCrucibleFormState {
    kind: CrucibleKind;
    /** Empty until the player picks one; the first (smallest) size stands in until then. */
    recipeId: string;
    /** Empty until the player picks one; the first authored crucible stands in until then. */
    templateId: string;
    displayName: string;
    saturation: SaturationChoice;
    /** A solo run is 1/1 and unlisted; the party size below is kept for when it's unchecked. */
    isSolo: boolean;
    /** The party size picked, or null for the most the choice takes. */
    maxPlayers: number | null;
}

interface CreateCruciblePanelProps {
    /** Closes the form without creating anything. */
    onCancel?: () => void;
    /** The character's readiness, to read against the chosen saturation's; null while unknown. */
    characterReadiness?: number | null;
    /** Told what the form would create whenever it changes, for the preview beside it (#224). */
    onDraftChange?: (draft: CrucibleCreateDraft) => void;
    /** Only Faint crucibles, never a roll, until the Mill Road commission is complete. */
    isSaturationLimited?: boolean;
}

/**
 * Form to create a new crucible (#223): a random one of a chosen size (each
 * gets its own generated map) or an authored one, then its name, its
 * saturation (picked, or random), and how many can join, up to what the
 * size or crucible takes. A run starts once everyone gathered is ready. Its
 * creator goes straight to its gathering (#345).
 */
export function CreateCruciblePanel({ onCancel, characterReadiness = null, onDraftChange, isSaturationLimited = false }: CreateCruciblePanelProps = {}): JSX.Element {
    const { recipes, authoredTemplates } = useCreateCrucibleOptions();
    const [createCrucible, { isLoading }] = useCreateCrucibleMutation();
    const [createError, setCreateError] = useState<string | null>(null);
    const { selectedCharacterId } = useSelectedCharacterId();
    const navigate = useAppNavigate();
    const [formState, setFormState] = useState<CreateCrucibleFormState>({
        kind: 'random',
        recipeId: '',
        templateId: '',
        displayName: '',
        saturation: 'random',
        isSolo: false,
        maxPlayers: null,
    });

    const hasAuthored = authoredTemplates.length > 0;
    const kind: CrucibleKind = formState.kind === 'authored' && hasAuthored ? 'authored' : 'random';
    const recipe = recipes.find((candidate) => candidate.id === formState.recipeId) ?? recipes[0] ?? null;
    const template = authoredTemplates.find((candidate) => candidate.id === formState.templateId) ?? authoredTemplates[0] ?? null;
    const choiceName = kind === 'random' ? recipe?.name : template?.displayName;
    const playerCap = resolvePlayerCap(kind === 'random' ? recipe?.maxPlayers : template?.maxPlayers);
    // A party holds at least 2; one player is a solo run, picked with its own toggle.
    const partySizes = Array.from({ length: Math.max(0, playerCap - 1) }, (_, index) => index + 2);
    const isSolo = formState.isSolo || partySizes.length === 0;
    const partySize = Math.min(Math.max(formState.maxPlayers ?? playerCap, 2), playerCap);
    const maxPlayers = isSolo ? 1 : partySize;
    const saturation: SaturationChoice = isSaturationLimited ? LIMITED_CRUCIBLE_SATURATION : formState.saturation;
    const saturationBands = isSaturationLimited
        ? VEIL_SATURATION_BANDS.filter((band) => band.id === LIMITED_CRUCIBLE_SATURATION)
        : VEIL_SATURATION_BANDS;
    const canCreate = !isLoading && (kind === 'random' ? recipe !== null : template !== null);
    // A random crucible's name, backstory and place, rolled now so the preview shows the real thing (#225).
    const { preview: flavor, reroll } = useCrucibleFlavorPreview(
        kind === 'random' ? recipe?.id ?? null : null,
        saturation === 'random' ? null : saturation,
    );
    const suggestedName = (kind === 'random' ? flavor?.name : undefined) ?? choiceName;

    useEffect(() => {
        onDraftChange?.({
            kind,
            name: formState.displayName,
            recipe: kind === 'random' ? recipe : null,
            template: kind === 'authored' ? template : null,
            saturation,
            isSolo,
            maxPlayers,
            flavor: kind === 'random' ? flavor : null,
        });
    }, [flavor, formState.displayName, saturation, isSolo, kind, maxPlayers, onDraftChange, recipe, template]);

    const handleCreate = useCallback(async (): Promise<void> => {
        if (!canCreate) {
            return;
        }
        setCreateError(null);
        try {
            const result = await createCrucible({
                ...(kind === 'random' ? { recipeId: recipe?.id, ...(flavor ? { flavorSeed: flavor.flavorSeed } : {}) } : { templateId: template?.id }),
                displayName: formState.displayName || undefined,
                // The selected character creates it, so entering it doesn't add a second one (#211).
                ...(selectedCharacterId ? { characterId: selectedCharacterId } : {}),
                maxPlayers,
                ...(saturation !== 'random' ? { veilSaturation: saturation } : {}),
            }).unwrap();
            // The creator is already in it, so go straight to its gathering (#345).
            navigate(toCruciblePath(result.id));
        } catch (failure) {
            // The form stays open, saying why, to try again.
            setCreateError(resolveApiErrorMessage(failure, 'The crucible could not be made. Try again.'));
        }
    }, [canCreate, createCrucible, flavor, formState, kind, maxPlayers, navigate, recipe, saturation, selectedCharacterId, template]);

    const chosenBand = saturation === 'random' ? null : resolveVeilSaturationById(saturation);
    const recommendedBand = characterReadiness !== null ? resolveRecommendedVeilSaturation(characterReadiness) : null;
    const kindTabs: TextTab<CrucibleKind>[] = [
        { id: 'random', label: 'Random' },
        { id: 'authored', label: 'Authored', disabled: !hasAuthored },
    ];

    return (
        <Panel className={styles.createPanel}>
            <h2 className={styles.heading}>Create Crucible</h2>

            <form
                className={styles.form}
                onSubmit={(e) => {
                    e.preventDefault();
                    void handleCreate();
                }}
            >
                <div className={styles.formGroup}>
                    <label className={styles.label} htmlFor="display-name">
                        Crucible Name
                    </label>
                    <div className={styles.nameField}>
                        <Textbox
                            id="display-name"
                            className={styles.input}
                            placeholder={suggestedName ? `Leave blank for "${suggestedName}"` : 'Optional'}
                            value={formState.displayName}
                            onChange={(e) => setFormState((s) => ({ ...s, displayName: e.target.value }))}
                        />
                        {/* Native button: it reads as plain text inside the field, unlike the project Button. */}
                        {kind === 'random' && flavor !== null && (
                            <button type="button" className={styles.rerollLink} onClick={reroll}>
                                Reroll
                            </button>
                        )}
                    </div>
                </div>

                <div className={styles.formGroup}>
                    <TextTabs
                        tabs={kindTabs}
                        activeTab={kind}
                        onTabChange={(nextKind) => setFormState((s) => ({ ...s, kind: nextKind, maxPlayers: null }))}
                        ariaLabel="Random or authored crucible"
                    />
                    {!hasAuthored && <p className={styles.hint}>There are no authored crucibles yet; every crucible is random.</p>}
                </div>

                {kind === 'random' ? (
                    <div className={styles.formGroup}>
                        <label className={styles.label} htmlFor="size-select">
                            Size
                        </label>
                        <Dropdown
                            id="size-select"
                            required
                            value={recipe?.id ?? ''}
                            onChange={(e) => setFormState((s) => ({ ...s, recipeId: e.target.value, maxPlayers: null }))}
                        >
                            {recipes.map((candidate) => (
                                <DropdownItem key={candidate.id} value={candidate.id}>
                                    {candidate.name}
                                </DropdownItem>
                            ))}
                        </Dropdown>
                    </div>
                ) : (
                    <div className={styles.formGroup}>
                        <label className={styles.label} htmlFor="template-select">
                            Crucible
                        </label>
                        <Dropdown
                            id="template-select"
                            required
                            value={template?.id ?? ''}
                            onChange={(e) => setFormState((s) => ({ ...s, templateId: e.target.value, maxPlayers: null }))}
                        >
                            {authoredTemplates.map((candidate) => (
                                <DropdownItem key={candidate.id} value={candidate.id}>
                                    {candidate.displayName}
                                </DropdownItem>
                            ))}
                        </Dropdown>
                    </div>
                )}

                <div className={styles.settingsRow}>
                    <div className={styles.formGroup}>
                        <label className={styles.label} htmlFor="saturation-select">
                            Saturation
                        </label>
                        <Dropdown
                            id="saturation-select"
                            value={saturation}
                            onChange={(e) => setFormState((s) => ({ ...s, saturation: e.target.value as SaturationChoice }))}
                        >
                            {!isSaturationLimited && <DropdownItem value="random">Random</DropdownItem>}
                            {saturationBands.map((band) => (
                                <DropdownItem key={band.id} value={band.id}>
                                    {`${band.displayName} - readiness ${formatRecommendedReadiness(band.recommendedReadiness)}`
                                        + (band.id === recommendedBand?.id ? ' (recommended)' : '')}
                                </DropdownItem>
                            ))}
                        </Dropdown>
                    </div>

                    <div className={styles.formGroup}>
                        <div className={styles.partyHeader}>
                            <label className={styles.label} htmlFor="max-players-select">
                                Party
                            </label>
                            <ToggleRow
                                id="solo-run-toggle"
                                label="Solo run"
                                className={styles.soloToggle}
                                checked={isSolo}
                                onChange={(nextIsSolo) => setFormState((s) => ({ ...s, isSolo: nextIsSolo }))}
                            />
                        </div>
                        <div className={styles.partySize}>
                            <span className={styles.partyPrefix}>Up to</span>
                            <Dropdown
                                id="max-players-select"
                                aria-label="Most players"
                                className={styles.partySelect}
                                disabled={isSolo}
                                value={maxPlayers}
                                onChange={(e) => setFormState((s) => ({ ...s, maxPlayers: Number(e.target.value) }))}
                            >
                                {/* A solo run shows 1 here; otherwise a party holds 2 up to the cap. */}
                                {(isSolo ? [1] : partySizes).map((size) => (
                                    <DropdownItem key={size} value={size}>{size}</DropdownItem>
                                ))}
                            </Dropdown>
                        </div>
                    </div>
                </div>
                <p className={styles.hint}>
                    {chosenBand === null
                        ? (kind === 'random' && flavor !== null
                            ? `Rolled: ${resolveVeilSaturationById(flavor.saturation).displayName}; fainter ones are likelier.`
                            : 'Saturation is rolled on creation; fainter ones are likelier.')
                            + (recommendedBand !== null ? ` ${recommendedBand.displayName} is recommended for you.` : '')
                        : `Recommended readiness ${formatRecommendedReadiness(chosenBand.recommendedReadiness)}`
                            + (characterReadiness !== null ? ` (you: ${characterReadiness})` : '')
                            + (isSaturationLimited ? '. Only Faint Crucibles until you finish the House of Exchange\'s next commission.' : '')}
                </p>

                {createError !== null && <p className={styles.error} role="alert">{createError}</p>}
                <div className={styles.actions}>
                    <Button type="button" variant="secondary" className={styles.cancelButton} onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={!canCreate}
                        className={styles.submitButton}
                    >
                        Create
                    </Button>
                </div>
            </form>
        </Panel>
    );
}
